import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/utils/api';
import {
  Upload, FileSpreadsheet, Download, CheckCircle2, AlertTriangle, XCircle, Loader2,
  ArrowRight, ArrowLeft, ChevronRight, Sparkles, RotateCcw, Database, ListChecks, Brain, Wand2
} from 'lucide-react';
import Layout from '@/components/Layout';
import AIColumnMapper from '@/components/AIColumnMapper';

const REQUIRED_SHEETS = ['Properties', 'Units', 'Tenants', 'Leases'];
const OPTIONAL_SHEETS = ['Payments', 'Maintenance', 'Documents'];
const ALL_SHEETS = [...REQUIRED_SHEETS, ...OPTIONAL_SHEETS];

export default function ExcelImport() {
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const [mode, setMode] = useState(null); // null = chooser · 'canonical' · 'ai'
  const [step, setStep] = useState(1); // 1=upload, 2=validate, 3=preview, 4=success
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);
  const [activeSheet, setActiveSheet] = useState('Properties');

  useEffect(() => { fetchHistory(); }, []);

  const fetchHistory = async () => {
    try { const r = await api.excelImportHistory(); setHistory(r.data.items || []); } catch { /* silent */ }
  };

  const downloadTemplate = async () => {
    try {
      const r = await api.downloadExcelTemplate();
      const blob = new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'REVA_Bulk_Import_Template.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setError(e?.response?.data?.detail || 'Download failed'); }
  };

  const handleFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.xlsx')) { setError(isAr ? 'يرجى اختيار ملف .xlsx' : 'Please choose a .xlsx file'); return; }
    if (f.size > 10 * 1024 * 1024) { setError(isAr ? 'الملف كبير (10MB كحد أقصى)' : 'File too large (10MB max)'); return; }
    setFile(f); setError('');
  };

  const submitPreview = async () => {
    if (!file) return;
    setUploading(true); setError('');
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.excelImportPreview(fd);
      setPreviewData(r.data);
      setStep(r.data.report.valid ? 3 : 2);
    } catch (e) {
      setError(e?.response?.data?.detail || (isAr ? 'فشل التحقق' : 'Validation failed'));
    } finally { setUploading(false); }
  };

  const submitConfirm = async () => {
    if (!previewData?.upload_id) return;
    setConfirming(true);
    try {
      const r = await api.excelImportConfirm(previewData.upload_id);
      setImportResult(r.data); setStep(4); fetchHistory();
    } catch (e) { setError(e?.response?.data?.detail || (isAr ? 'فشل الاستيراد' : 'Import failed')); }
    finally { setConfirming(false); }
  };

  const reset = () => {
    setStep(1); setFile(null); setPreviewData(null); setImportResult(null); setError(''); setActiveSheet('Properties');
  };

  // Step indicator
  const steps = [
    { id: 1, label: isAr ? 'رفع' : 'Upload' },
    { id: 2, label: isAr ? 'تحقق' : 'Validate' },
    { id: 3, label: isAr ? 'معاينة' : 'Preview' },
    { id: 4, label: isAr ? 'نجح' : 'Success' },
  ];

  return (
    <Layout>
      <div className="space-y-6 max-w-5xl mx-auto">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center"><FileSpreadsheet className="h-5 w-5 text-emerald-700" strokeWidth={1.5} /></div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-gray-900">{isAr ? 'استيراد Excel' : 'Excel Workbook Import'}</h1>
              <p className="text-sm text-gray-500">{isAr ? 'ارفع ملف Excel واحد بسبع صفحات (مطلوبة: العقارات / الوحدات / المستأجرون / العقود — اختيارية: الدفعات / الصيانة / المستندات)' : 'Upload one Excel with up to 7 sheets (required: Properties / Units / Tenants / Leases — optional: Payments / Maintenance / Documents)'}</p>
            </div>
          </div>
          <Button onClick={downloadTemplate} variant="outline" className="rounded-xl" data-testid="excel-download-template">
            <Download className="h-4 w-4 me-2" /> {isAr ? 'تحميل القالب' : 'Download template'}
          </Button>
        </div>

        {/* Mode chooser — first thing users see */}
        {!mode && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => setMode('ai')}
              className="group relative text-start p-6 rounded-2xl bg-gradient-to-br from-primary/5 via-white to-amber-50/40 border-2 border-primary/15 hover:border-primary/40 hover:shadow-hover transition-all overflow-hidden"
              data-testid="mode-ai-card"
            >
              <div className="absolute top-3 end-3 px-2 py-0.5 bg-amber-400 text-amber-900 text-[10px] font-bold uppercase tracking-wider rounded-full">{isAr ? 'مستحسن' : 'Recommended'}</div>
              <div className="w-12 h-12 bg-primary/10 group-hover:bg-primary/20 rounded-xl flex items-center justify-center mb-4 transition-colors">
                <Wand2 className="h-6 w-6 text-primary" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-lg font-bold text-gray-900 mb-1.5">{isAr ? 'مطابقة الأعمدة بالذكاء الاصطناعي' : 'AI-assisted column mapping'}</h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-3">
                {isAr
                  ? 'ارفع ملفك الحالي بأي تنسيق — REVA يكتشف الأعمدة تلقائياً بالعربية والإنجليزية ويربطها ببنيتنا.'
                  : 'Upload your existing file in any format — REVA auto-detects columns in Arabic & English and maps them to our schema.'}
              </p>
              <div className="text-xs font-medium text-primary flex items-center gap-1.5">
                {isAr ? 'ابدأ هنا' : 'Start here'} <ArrowRight className="h-3.5 w-3.5" />
              </div>
            </button>

            <button
              onClick={() => setMode('canonical')}
              className="group text-start p-6 rounded-2xl bg-white border-2 border-gray-200 hover:border-primary/30 hover:shadow-soft transition-all"
              data-testid="mode-canonical-card"
            >
              <div className="w-12 h-12 bg-gray-100 group-hover:bg-gray-200 rounded-xl flex items-center justify-center mb-4 transition-colors">
                <FileSpreadsheet className="h-6 w-6 text-gray-700" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-lg font-bold text-gray-900 mb-1.5">{isAr ? 'استخدم قالب REVA' : 'Use the REVA template'}</h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-3">
                {isAr
                  ? 'حمّل قالبنا الجاهز بـ7 صفحات معيارية، املأه، وارفعه — أسرع طريقة إذا كنت تبدأ من الصفر.'
                  : 'Download our 7-sheet canonical template, fill it in, upload — fastest path if you\'re starting from scratch.'}
              </p>
              <div className="text-xs font-medium text-gray-700 flex items-center gap-1.5">
                {isAr ? 'الطريقة المعيارية' : 'Standard path'} <ArrowRight className="h-3.5 w-3.5" />
              </div>
            </button>
          </div>
        )}

        {/* AI mode — handled by AIColumnMapper component */}
        {mode === 'ai' && (
          <AIColumnMapper isAr={isAr} onBack={() => setMode(null)} onComplete={() => fetchHistory()} />
        )}

        {/* Canonical mode — original 4-step stepper */}
        {mode === 'canonical' && (<>
        {/* Stepper */}
        <Card className="shadow-soft border-gray-100">
          <CardContent className="pt-5 pb-5">
            <div className="flex items-center justify-between">
              {steps.map((s, i) => (
                <React.Fragment key={s.id}>
                  <div className="flex items-center gap-2">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step >= s.id ? 'bg-primary text-white' : 'bg-gray-100 text-gray-400'}`}>
                      {step > s.id ? <CheckCircle2 className="h-4 w-4" /> : s.id}
                    </div>
                    <span className={`text-xs font-medium ${step >= s.id ? 'text-gray-900' : 'text-gray-400'}`}>{s.label}</span>
                  </div>
                  {i < steps.length - 1 && <div className={`flex-1 h-0.5 mx-2 ${step > s.id ? 'bg-primary' : 'bg-gray-100'}`} />}
                </React.Fragment>
              ))}
            </div>
          </CardContent>
        </Card>

        {error && <div className="text-sm text-red-700 bg-red-50 border border-red-100 px-4 py-2.5 rounded-xl flex items-start gap-2" data-testid="excel-error"><AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />{error}</div>}

        {/* Step 1 — Upload */}
        {step === 1 && (
          <Card className="shadow-soft border-gray-100">
            <CardHeader><CardTitle className="font-heading text-base">{isAr ? 'الخطوة 1 — اختر ملف Excel' : 'Step 1 — Choose your Excel file'}</CardTitle></CardHeader>
            <CardContent>
              <label className="block border-2 border-dashed border-gray-200 rounded-2xl p-10 text-center hover:border-primary/40 transition-colors cursor-pointer" data-testid="excel-drop">
                <input type="file" className="hidden" accept=".xlsx" onChange={e => handleFile(e.target.files[0])} data-testid="excel-file-input" />
                <Upload className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                {file ? (
                  <div>
                    <p className="font-semibold text-primary">{file.name}</p>
                    <p className="text-xs text-gray-500 mt-1">{(file.size / 1024).toFixed(1)} KB</p>
                  </div>
                ) : (
                  <div>
                    <p className="font-medium text-gray-900">{isAr ? 'اسحب وأفلت أو اضغط للاختيار' : 'Drag & drop or click to choose'}</p>
                    <p className="text-xs text-gray-500 mt-1">{isAr ? 'فقط ملفات .xlsx — حد أقصى 10MB' : '.xlsx files only · 10MB max'}</p>
                  </div>
                )}
              </label>

              <div className="mt-5 p-4 bg-blue-50 border border-blue-100 rounded-xl flex items-start gap-3">
                <ListChecks className="h-4 w-4 text-blue-700 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-blue-900 leading-relaxed">
                  <div className="font-bold mb-1">{isAr ? 'الصفحات المدعومة' : 'Supported sheets'}</div>
                  <div className="mt-2">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-blue-700 mb-1">{isAr ? 'مطلوبة' : 'Required'}</div>
                    <div className="flex flex-wrap gap-1.5">
                      {REQUIRED_SHEETS.map(s => <span key={s} className="px-2 py-0.5 bg-white rounded text-blue-700 font-medium text-[11px]">{s}</span>)}
                    </div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-blue-700 mb-1 mt-2">{isAr ? 'اختيارية' : 'Optional'}</div>
                    <div className="flex flex-wrap gap-1.5">
                      {OPTIONAL_SHEETS.map(s => <span key={s} className="px-2 py-0.5 bg-white rounded text-emerald-700 font-medium text-[11px]">{s}</span>)}
                    </div>
                  </div>
                  <div className="mt-2 text-blue-700">{isAr ? 'حمّل القالب لرؤية الأعمدة المطلوبة بالأمثلة لكل صفحة.' : 'Download the template to see required columns with examples for every sheet.'}</div>
                </div>
              </div>

              <div className="mt-5 flex justify-end">
                <Button onClick={submitPreview} disabled={!file || uploading} className="gradient-primary text-white rounded-xl" data-testid="excel-validate-btn">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : <ArrowRight className="h-4 w-4 me-2" />}
                  {isAr ? 'تحقق وعاين' : 'Validate & preview'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2/3 — Validation + Preview */}
        {(step === 2 || step === 3) && previewData && (
          <>
            <Card className="shadow-soft border-gray-100">
              <CardHeader><CardTitle className="font-heading text-base">{isAr ? 'نتيجة التحقق' : 'Validation Results'}</CardTitle></CardHeader>
              <CardContent>
                {previewData.report.missing_sheets?.length > 0 && (
                  <div className="p-3 bg-red-50 border border-red-100 rounded-xl mb-4 flex items-start gap-2">
                    <XCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-red-800"><b>{isAr ? 'صفحات مفقودة:' : 'Missing sheets:'}</b> {previewData.report.missing_sheets.join(', ')}</div>
                  </div>
                )}

                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                  {ALL_SHEETS.map(s => {
                    const ps = previewData.report.per_sheet?.[s];
                    if (!ps) return null;
                    const errs = ps.errors?.length || 0;
                    const warns = ps.warnings?.length || 0;
                    const ok = errs === 0;
                    const isOptional = OPTIONAL_SHEETS.includes(s);
                    return (
                      <button key={s} onClick={() => setActiveSheet(s)} className={`p-4 rounded-xl border-2 text-start transition-all ${activeSheet === s ? 'border-primary bg-primary/5' : 'border-gray-100 bg-white hover:border-gray-200'}`} data-testid={`excel-sheet-${s}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-bold text-gray-500 uppercase tracking-wider truncate">{s}</span>
                          {ok ? <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" /> : <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0" />}
                        </div>
                        <div className="text-2xl font-heading font-bold text-gray-900">{previewData.report.counts?.[s] || 0}</div>
                        <div className="text-[10px] text-gray-500">{ps.rows} {isAr ? 'صف' : 'rows'} · {errs} {isAr ? 'خطأ' : 'err'} · {warns} {isAr ? 'تنبيه' : 'warn'}</div>
                        {isOptional && <div className="text-[9px] text-emerald-700 font-bold uppercase tracking-wider mt-1">{isAr ? 'اختياري' : 'Optional'}</div>}
                      </button>
                    );
                  })}
                </div>

                {/* Issues for active sheet */}
                {previewData.report.per_sheet?.[activeSheet] && (
                  <div className="mt-5 space-y-3">
                    {previewData.report.per_sheet[activeSheet].missing_columns?.length > 0 && (
                      <div className="p-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-800"><b>{isAr ? 'أعمدة مفقودة:' : 'Missing columns:'}</b> {previewData.report.per_sheet[activeSheet].missing_columns.join(', ')}</div>
                    )}
                    {previewData.report.per_sheet[activeSheet].errors?.length > 0 && (
                      <div className="space-y-1">
                        <div className="text-xs font-bold text-red-700 uppercase tracking-wider">{isAr ? 'الأخطاء' : 'Errors'} ({previewData.report.per_sheet[activeSheet].errors.length})</div>
                        <div className="max-h-40 overflow-y-auto space-y-1">
                          {previewData.report.per_sheet[activeSheet].errors.slice(0, 20).map((err, i) => (
                            <div key={i} className="text-xs p-2 bg-red-50 rounded">Row {err.row}: {err.msg}</div>
                          ))}
                        </div>
                      </div>
                    )}
                    {previewData.report.per_sheet[activeSheet].warnings?.length > 0 && (
                      <div className="space-y-1">
                        <div className="text-xs font-bold text-amber-700 uppercase tracking-wider">{isAr ? 'تنبيهات' : 'Warnings'} ({previewData.report.per_sheet[activeSheet].warnings.length})</div>
                        <div className="max-h-32 overflow-y-auto space-y-1">
                          {previewData.report.per_sheet[activeSheet].warnings.slice(0, 10).map((w, i) => (
                            <div key={i} className="text-xs p-2 bg-amber-50 rounded">Row {w.row}: {w.msg}</div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Sample preview */}
                    {previewData.sample?.[activeSheet]?.length > 0 && (
                      <div>
                        <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">{isAr ? 'أول 5 صفوف' : 'First 5 rows'}</div>
                        <div className="overflow-x-auto border border-gray-100 rounded-xl">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                {Object.keys(previewData.sample[activeSheet][0] || {}).slice(0, 7).map(k => <TableHead key={k} className="text-xs">{k}</TableHead>)}
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {previewData.sample[activeSheet].map((row, i) => (
                                <TableRow key={i}>
                                  {Object.keys(row).slice(0, 7).map(k => <TableCell key={k} className="text-xs">{String(row[k] ?? '—')}</TableCell>)}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex justify-between mt-6 pt-5 border-t">
                  <Button onClick={reset} variant="outline" className="rounded-xl" data-testid="excel-back-btn">
                    <ArrowLeft className="h-4 w-4 me-2" /> {isAr ? 'إعادة' : 'Start over'}
                  </Button>
                  <Button onClick={submitConfirm} disabled={!previewData.report.valid || confirming} className="gradient-primary text-white rounded-xl" data-testid="excel-confirm-btn">
                    {confirming ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : <Database className="h-4 w-4 me-2" />}
                    {isAr ? `استيراد ${Object.values(previewData.report.counts || {}).reduce((a, b) => a + b, 0)} سجل` : `Import ${Object.values(previewData.report.counts || {}).reduce((a, b) => a + b, 0)} records`}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Step 4 — Success */}
        {step === 4 && importResult && (
          <Card className="shadow-glass border-green-200 bg-gradient-to-br from-green-50/60 via-white to-primary/5">
            <CardContent className="py-10">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <CheckCircle2 className="h-9 w-9 text-green-600" />
                </div>
                <h2 className="font-heading text-2xl font-bold text-gray-900">{isAr ? 'اكتمل الاستيراد!' : 'Import complete!'}</h2>
                <p className="text-sm text-gray-500 mt-1">{isAr ? 'تم إضافة جميع البيانات إلى منظمتك بنجاح.' : 'All records have been added to your organization.'}</p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 max-w-4xl mx-auto">
                {Object.entries(importResult.inserted || {}).map(([k, v]) => (
                  <div key={k} className="p-4 bg-white rounded-xl border border-gray-100 text-center" data-testid={`success-${k}`}>
                    <div className="text-3xl font-heading font-bold text-primary">{v}</div>
                    <div className="text-xs text-gray-500 uppercase tracking-wider mt-1">{k}</div>
                  </div>
                ))}
              </div>
              {Object.values(importResult.failed || {}).some(v => v > 0) && (
                <div className="mt-5 p-3 bg-amber-50 border border-amber-100 rounded-xl text-sm text-amber-800 max-w-2xl mx-auto">
                  <b>{isAr ? 'تخطي:' : 'Skipped:'}</b> {Object.entries(importResult.failed).filter(([_, v]) => v > 0).map(([k, v]) => `${v} ${k}`).join(', ')}
                </div>
              )}
              <div className="flex justify-center gap-3 mt-6">
                <Button onClick={reset} variant="outline" className="rounded-xl" data-testid="excel-import-another">
                  <RotateCcw className="h-4 w-4 me-2" /> {isAr ? 'استيراد آخر' : 'Import another'}
                </Button>
                <Button onClick={() => window.location.href = '/properties'} className="gradient-primary text-white rounded-xl" data-testid="excel-view-data">
                  {isAr ? 'عرض العقارات' : 'View properties'} <ChevronRight className="h-4 w-4 ms-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* History */}
        {history.length > 0 && (
          <Card className="shadow-soft border-gray-100">
            <CardHeader><CardTitle className="font-heading text-base">{isAr ? 'السجل' : 'History'}</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {history.slice(0, 5).map(h => (
                  <div key={h.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl text-xs">
                    <div className="flex items-center gap-2">
                      <FileSpreadsheet className="h-3.5 w-3.5 text-emerald-600" />
                      <span className="font-medium text-gray-900">{h.file_name}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase font-bold ${h.status === 'imported' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>{h.status}</span>
                    </div>
                    <div className="text-gray-400">{new Date(h.created_at).toLocaleString()}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        </>)}
      </div>
    </Layout>
  );
}
